import type { PostHog } from 'posthog-js'
import posthog from 'posthog-js'

import type { OrganizationGroupProperties, PiboEvent } from '../events'
import { ORGANIZATION_GROUP } from '../events'

export interface PosthogClientConfig {
  /** NEXT_PUBLIC_POSTHOG_KEY from the consuming app's validated env. */
  key?: string
  /** NEXT_PUBLIC_POSTHOG_HOST from the consuming app's validated env. */
  host: string
  /** NEXT_PUBLIC_POSTHOG_UI_HOST from the consuming app's validated env. */
  uiHost: string
  /**
   * Automatic pageview capture. Leave on for the marketing site; turn OFF for
   * the web/school SPAs where value-events (book_opened, reading_*) are the
   * real signal and route changes are noise.
   */
  capturePageview?: boolean
  /**
   * Automatic `$pageleave` capture (fires on unload/route change with the time
   * spent on the page). On by default — it's the engagement signal even when
   * pageview capture is off, so it does not follow `capturePageview`.
   */
  capturePageleave?: boolean
  /**
   * Autocapture Core Web Vitals ($web_vitals: LCP, INP, CLS, FCP) — real-user
   * performance that also feeds Google search ranking. On by default.
   */
  captureWebVitals?: boolean
  /**
   * Exception autocapture (unhandled errors and promise rejections → `$exception`). On by default;
   * turn OFF where visitors are anonymous, since the point is tying an error to a person.
   */
  captureExceptions?: boolean
  debug?: boolean
}

let initialized = false

/** An account is "new" — and its sessions are recorded — for this many days after it was created. */
export const SESSION_RECORDING_MAX_ACCOUNT_AGE_DAYS = 7

/**
 * Whose replay a PostHog session id carries, persisted so it survives a reload: the session id
 * outlives the page, and a new person on a shared device must not continue someone else's replay.
 */
const RECORDING_OWNER_KEY = 'pibo_recording_owner'

interface RecordingOwner {
  distinctId: string
  sessionId: string
}

/** Whether the identified user's account is new enough to record. Set by `syncSessionRecording`. */
let recordingEligible = false
/** Open `suppressSessionRecording` holds (the book reader). Any open hold keeps recording off. */
let recordingSuppressions = 0

function ready(): boolean {
  return typeof window !== 'undefined' && initialized
}

/**
 * Initialize posthog-js. No-op on the server, when already initialized, or when
 * no key is configured (analytics simply stays disabled). Call once at app boot
 * — alongside Sentry.init in the Vite routers, or inside a Next provider.
 */
export function initPosthog(config: PosthogClientConfig): PostHog | undefined {
  if (typeof window === 'undefined') return undefined
  if (initialized) return posthog
  if (!config.key) return undefined

  posthog.init(config.key, {
    api_host: config.host,
    ui_host: config.uiHost,
    defaults: '2026-05-30',
    capture_pageview: config.capturePageview ?? true,
    capture_pageleave: config.capturePageleave ?? true,
    capture_performance: { web_vitals: config.captureWebVitals ?? true },
    // Only create person profiles for identified users — keeps anonymous
    // marketing traffic from inflating the billed profile count.
    person_profiles: 'identified_only',
    // We fire explicit, typed events; autocapture adds noise.
    autocapture: false,
    // Replay is billed per recording, so nothing records by default even though the project setting
    // is on. `syncSessionRecording` starts it for new accounts only, once the user is identified.
    disable_session_recording: true,
    // Unhandled errors and promise rejections become `$exception` events on the identified person,
    // so an error can be traced to who hit it (and to their replay, when one is recording).
    // Sentry still captures the same errors; this copy is for the person-level view.
    capture_exceptions: config.captureExceptions ?? true,
    debug: config.debug ?? false,
  })
  initialized = true
  return posthog
}

/** Capture a typed event. Safe to call before init (no-op). */
export function capture(event: PiboEvent): void {
  if (!ready()) return
  posthog.capture(event.name, event.props)
}

/**
 * Associate subsequent events with a user. Call on signup, on every login, and
 * on PIN-switch to a child (pass the child's userId). Idempotent.
 */
export function identifyUser(distinctId: string, properties?: Record<string, unknown>): void {
  if (!ready()) return
  posthog.identify(distinctId, properties)
}

/**
 * Clear the current identity. MUST be called on logout — critical on shared
 * devices (e.g. a school tablet) so the next user's events don't attach to the
 * previous person.
 */
export function resetAnalytics(): void {
  if (!ready()) return
  // Stop before reset: reset rotates the session id but leaves recording enabled, so the next
  // person on the device would otherwise be recorded under a fresh session.
  recordingEligible = false
  posthog.stopSessionRecording()
  writeRecordingOwner(null)
  posthog.reset()
}

function isNewAccount(createdAt: Date | string | null | undefined): boolean {
  if (!createdAt) return false
  const createdMs = new Date(createdAt).getTime()
  if (Number.isNaN(createdMs)) return false
  return Date.now() - createdMs <= SESSION_RECORDING_MAX_ACCOUNT_AGE_DAYS * 24 * 60 * 60 * 1000
}

function readRecordingOwner(): RecordingOwner | null {
  try {
    const raw = window.localStorage.getItem(RECORDING_OWNER_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'distinctId' in parsed &&
      'sessionId' in parsed &&
      typeof parsed.distinctId === 'string' &&
      typeof parsed.sessionId === 'string'
    ) {
      return { distinctId: parsed.distinctId, sessionId: parsed.sessionId }
    }
    return null
  } catch {
    return null
  }
}

function writeRecordingOwner(owner: RecordingOwner | null): void {
  try {
    if (owner) window.localStorage.setItem(RECORDING_OWNER_KEY, JSON.stringify(owner))
    else window.localStorage.removeItem(RECORDING_OWNER_KEY)
  } catch {
    // Storage blocked (private mode): the in-session switch still stops the old recording.
  }
}

function applySessionRecording(): void {
  if (!recordingEligible || recordingSuppressions > 0) {
    // Unconditional: `sessionRecordingStarted()` is false while a start still waits on remote
    // config, and that pending start must be cancelled too.
    posthog.stopSessionRecording()
    return
  }

  const distinctId = posthog.get_distinct_id()
  const owner = readRecordingOwner()
  if (owner && owner.distinctId !== distinctId && owner.sessionId === posthog.get_session_id()) {
    // This session id already carries someone else's replay — give the new person their own.
    posthog.stopSessionRecording()
    posthog.sessionManager?.resetSessionId()
  }
  writeRecordingOwner({ distinctId, sessionId: posthog.get_session_id() })
  // No override argument: it only bypasses remote sampling / linked-flag / trigger controls, and
  // those stay in the PostHog project settings as a second throttle.
  if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording()
}

/**
 * Record the session only while the identified user's account is new. Call right after
 * `identifyUser`, with the `createdAt` of that same user — on a PIN switch that is the profile
 * switched to. Safe to call before init (no-op).
 *
 * A switch between two new profiles on a shared device rotates the session id before recording
 * again, so one person's replay never continues into the next person's — also across a reload.
 */
export function syncSessionRecording(createdAt: Date | string | null | undefined): void {
  if (!ready()) return
  recordingEligible = isNewAccount(createdAt)
  applySessionRecording()
}

/**
 * Stop recording ahead of an identity change the caller knows is about to land (an accepted
 * profile switch), so the next profile's first render never reaches the previous person's replay.
 * The next `syncSessionRecording` decides whether it resumes. Safe to call before init (no-op).
 */
export function stopSessionRecording(): void {
  if (!ready()) return
  recordingEligible = false
  applySessionRecording()
}

/**
 * Keep recording off while a surface that is too heavy to replay is open — the book reader is most
 * of the replay volume. Returns the release; use as `useEffect(() => suppressSessionRecording(), [])`.
 * Recording resumes on release if the user is still eligible. Safe to call before init (no-op).
 */
export function suppressSessionRecording(): () => void {
  recordingSuppressions += 1
  if (ready()) applySessionRecording()

  let released = false
  return () => {
    if (released) return
    released = true
    recordingSuppressions -= 1
    if (ready()) applySessionRecording()
  }
}

/** Attach subsequent events to an organization group (family/school). */
export function setOrganizationGroup(organizationId: string, properties?: OrganizationGroupProperties): void {
  if (!ready()) return
  posthog.group(ORGANIZATION_GROUP, organizationId, properties)
}

export { posthog }
export type { PiboEvent, OrganizationGroupProperties }
